/**
 * Service-side permission catalog. Mirrors `attunelogic-api`
 * `src/constants/access-control.js`. The API is the source of truth and
 * always re-validates permissions, so this file only powers UI gating and
 * the Roles & Permissions configuration UI.
 */
export const PERMISSIONS = {
  JOBS_READ: "jobs.read",
  JOBS_CREATE: "jobs.create",
  JOBS_EDIT: "jobs.edit",
  JOBS_DELETE: "jobs.delete",
  JOBS_ASSIGN: "jobs.assign",

  DISPATCH_READ: "dispatch.read",
  DISPATCH_MANAGE: "dispatch.manage",

  INVOICES_READ: "invoices.read",
  INVOICES_CREATE: "invoices.create",
  INVOICES_EDIT: "invoices.edit",
  INVOICES_DELETE: "invoices.delete",
  INVOICES_SEND: "invoices.send",

  // Inventory permissions are retired. The keys remain defined so any
  // historical references still resolve to a string, but they are not part
  // of the active catalog and no caller should gate on them. Inventory
  // pages rely on the `inventory.enabled` feature flag instead.
  INVENTORY_READ: "inventory.read",
  INVENTORY_CREATE: "inventory.create",
  INVENTORY_EDIT: "inventory.edit",
  INVENTORY_DELETE: "inventory.delete",

  MANIFESTS_READ: "manifests.read",
  MANIFESTS_CREATE: "manifests.create",
  MANIFESTS_EDIT: "manifests.edit",
  MANIFESTS_DELETE: "manifests.delete",

  CLIENTS_READ: "clients.read",
  CLIENTS_CREATE: "clients.create",
  CLIENTS_EDIT: "clients.edit",
  CLIENTS_DELETE: "clients.delete",

  DRIVERS_READ: "drivers.read",
  DRIVERS_MANAGE: "drivers.manage",

  PAYOUTS_READ: "payouts.read",
  PAYOUTS_CREATE: "payouts.create",
  PAYOUTS_EDIT: "payouts.edit",
  PAYOUTS_DELETE: "payouts.delete",

  EMPLOYEES_READ: "employees.read",
  EMPLOYEES_MANAGE: "employees.manage",

  EQUIPMENT_READ: "equipment.read",
  EQUIPMENT_CREATE: "equipment.create",
  EQUIPMENT_EDIT: "equipment.edit",
  EQUIPMENT_DELETE: "equipment.delete",

  FLEET_VIEW: "fleet.view",
  FLEET_MANAGE: "fleet.manage",
  INTEGRATIONS_MOTIVE_MANAGE: "integrations.motive.manage",

  TASKS_READ: "tasks.read",
  TASKS_MANAGE: "tasks.manage",

  SUPPLIERS_READ: "suppliers.read",
  SUPPLIERS_MANAGE: "suppliers.manage",

  MAINTENANCE_READ: "maintenance.read",
  MAINTENANCE_MANAGE: "maintenance.manage",

  REPORTS_READ: "reports.read",
  REPORTS_CREATE: "reports.create",

  SETTINGS_USERS_READ: "settings.users.read",
  SETTINGS_USERS_MANAGE: "settings.users.manage",
  SETTINGS_ROLES_READ: "settings.roles.read",
  SETTINGS_ROLES_MANAGE: "settings.roles.manage",
  SETTINGS_BILLING_READ: "settings.billing.read",
  SETTINGS_BILLING_MANAGE: "settings.billing.manage",
  SETTINGS_TAGS_MANAGE: "settings.tags.manage",
  SETTINGS_TEMPLATES_MANAGE: "settings.templates.manage",
  SETTINGS_ORGANIZATION_MANAGE: "settings.organization.manage",
  SETTINGS_ADVANCED_MANAGE: "settings.advanced.manage",
};

const IMPLIED_PERMISSIONS = {
  [PERMISSIONS.JOBS_CREATE]: [PERMISSIONS.JOBS_READ],
  [PERMISSIONS.JOBS_EDIT]: [PERMISSIONS.JOBS_READ],
  [PERMISSIONS.JOBS_DELETE]: [PERMISSIONS.JOBS_READ],
  [PERMISSIONS.JOBS_ASSIGN]: [PERMISSIONS.JOBS_READ],
  [PERMISSIONS.DISPATCH_MANAGE]: [PERMISSIONS.DISPATCH_READ],
  [PERMISSIONS.INVOICES_CREATE]: [PERMISSIONS.INVOICES_READ],
  [PERMISSIONS.INVOICES_EDIT]: [PERMISSIONS.INVOICES_READ],
  [PERMISSIONS.INVOICES_DELETE]: [PERMISSIONS.INVOICES_READ],
  [PERMISSIONS.INVOICES_SEND]: [PERMISSIONS.INVOICES_READ],
  [PERMISSIONS.MANIFESTS_CREATE]: [PERMISSIONS.MANIFESTS_READ],
  [PERMISSIONS.MANIFESTS_EDIT]: [PERMISSIONS.MANIFESTS_READ],
  [PERMISSIONS.MANIFESTS_DELETE]: [PERMISSIONS.MANIFESTS_READ],
  [PERMISSIONS.CLIENTS_CREATE]: [PERMISSIONS.CLIENTS_READ],
  [PERMISSIONS.CLIENTS_EDIT]: [PERMISSIONS.CLIENTS_READ],
  [PERMISSIONS.CLIENTS_DELETE]: [PERMISSIONS.CLIENTS_READ],
  [PERMISSIONS.DRIVERS_MANAGE]: [PERMISSIONS.DRIVERS_READ],
  [PERMISSIONS.PAYOUTS_CREATE]: [PERMISSIONS.PAYOUTS_READ],
  [PERMISSIONS.PAYOUTS_EDIT]: [PERMISSIONS.PAYOUTS_READ],
  [PERMISSIONS.PAYOUTS_DELETE]: [PERMISSIONS.PAYOUTS_READ],
  [PERMISSIONS.EMPLOYEES_MANAGE]: [PERMISSIONS.EMPLOYEES_READ],
  [PERMISSIONS.EQUIPMENT_CREATE]: [PERMISSIONS.EQUIPMENT_READ],
  [PERMISSIONS.EQUIPMENT_EDIT]: [PERMISSIONS.EQUIPMENT_READ],
  [PERMISSIONS.EQUIPMENT_DELETE]: [PERMISSIONS.EQUIPMENT_READ],
  [PERMISSIONS.FLEET_MANAGE]: [PERMISSIONS.FLEET_VIEW],
  [PERMISSIONS.INTEGRATIONS_MOTIVE_MANAGE]: [
    PERMISSIONS.FLEET_VIEW,
    PERMISSIONS.EQUIPMENT_READ,
  ],
  [PERMISSIONS.TASKS_MANAGE]: [PERMISSIONS.TASKS_READ],
  [PERMISSIONS.SUPPLIERS_MANAGE]: [PERMISSIONS.SUPPLIERS_READ],
  [PERMISSIONS.MAINTENANCE_MANAGE]: [PERMISSIONS.MAINTENANCE_READ],
  [PERMISSIONS.REPORTS_CREATE]: [PERMISSIONS.REPORTS_READ],
  [PERMISSIONS.SETTINGS_USERS_MANAGE]: [PERMISSIONS.SETTINGS_USERS_READ],
  [PERMISSIONS.SETTINGS_ROLES_MANAGE]: [PERMISSIONS.SETTINGS_ROLES_READ],
  [PERMISSIONS.SETTINGS_BILLING_MANAGE]: [PERMISSIONS.SETTINGS_BILLING_READ],
};

/**
 * Permission keys that used to be in the catalog but have been retired.
 * Kept as constants for backwards compatibility with any historical role
 * data, but excluded from `ALL_PERMISSION_KEYS` so they no longer flow
 * into the owner/admin authority default sets.
 */
const RETIRED_PERMISSIONS = new Set([
  PERMISSIONS.INVENTORY_READ,
  PERMISSIONS.INVENTORY_CREATE,
  PERMISSIONS.INVENTORY_EDIT,
  PERMISSIONS.INVENTORY_DELETE,
]);

/**
 * Default permission sets used for the legacy authority ladder. Mirrors the
 * API so the UI can gate consistently even before the tenant-role backfill
 * has run for a customer.
 */
const ALL_PERMISSION_KEYS = Object.values(PERMISSIONS).filter(
  (key) => !RETIRED_PERMISSIONS.has(key)
);

const ADMIN_AUTHORITY_PERMISSIONS = ALL_PERMISSION_KEYS.filter(
  (key) =>
    key !== PERMISSIONS.SETTINGS_BILLING_MANAGE &&
    key !== PERMISSIONS.SETTINGS_ADVANCED_MANAGE
);

const USER_AUTHORITY_PERMISSIONS = [
  PERMISSIONS.JOBS_READ,
  PERMISSIONS.JOBS_CREATE,
  PERMISSIONS.JOBS_EDIT,
  PERMISSIONS.DISPATCH_READ,
  PERMISSIONS.INVOICES_READ,
  PERMISSIONS.INVOICES_CREATE,
  PERMISSIONS.MANIFESTS_READ,
  PERMISSIONS.MANIFESTS_CREATE,
  PERMISSIONS.MANIFESTS_EDIT,
  PERMISSIONS.CLIENTS_READ,
  PERMISSIONS.DRIVERS_READ,
  PERMISSIONS.EMPLOYEES_READ,
  PERMISSIONS.EQUIPMENT_READ,
  PERMISSIONS.EQUIPMENT_EDIT,
  PERMISSIONS.TASKS_READ,
  PERMISSIONS.TASKS_MANAGE,
  PERMISSIONS.SUPPLIERS_READ,
  PERMISSIONS.MAINTENANCE_READ,
  PERMISSIONS.MAINTENANCE_MANAGE,
  PERMISSIONS.REPORTS_READ,
];

const CLIENT_AUTHORITY_PERMISSIONS = [
  PERMISSIONS.JOBS_READ,
  PERMISSIONS.INVOICES_READ,
  PERMISSIONS.REPORTS_READ,
];

const AUTHORITY_PERMISSIONS = {
  owner: ALL_PERMISSION_KEYS,
  admin: ADMIN_AUTHORITY_PERMISSIONS,
  user: USER_AUTHORITY_PERMISSIONS,
  client: CLIENT_AUTHORITY_PERMISSIONS,
};

/**
 * Expand a permission list by including any implied read permissions.
 *
 * @param {string[]} permissions
 * @returns {string[]}
 */
export const expandPermissions = (permissions = []) => [
  ...new Set(
    permissions.flatMap((permission) => [
      permission,
      ...(IMPLIED_PERMISSIONS[permission] || []),
    ])
  ),
];

/**
 * Returns the default permissions that the API resolver would apply for a
 * user's legacy authority when they have no tenant roles assigned. Used by
 * UI helpers as a safety net during the tenant-role rollout.
 *
 * @param {string} authority
 * @returns {string[]}
 */
export const getDefaultPermissionsForAuthority = (authority) =>
  AUTHORITY_PERMISSIONS[authority] || [];

/**
 * Returns the effective permission list for a user, layering authority
 * defaults (when no tenant roles are assigned) on top of any access object
 * returned by the API.
 *
 * @param {object} user
 * @returns {string[]}
 */
export const getEffectivePermissions = (user) => {
  if (!user) return [];
  const apiPermissions = user?.access?.permissions || [];
  const hasTenantRoles = Array.isArray(user.tenantRoles) && user.tenantRoles.length > 0;
  const fallback = hasTenantRoles
    ? []
    : getDefaultPermissionsForAuthority(user.authority);
  return expandPermissions([...apiPermissions, ...fallback]);
};

/**
 * Permission gate used by client-side guards. Always treat the API resolver
 * response as authoritative; this helper is a UX convenience that mirrors the
 * server logic.
 *
 * @param {object} user
 * @param {string} permission
 * @returns {boolean}
 */
export const hasAccessPermission = (user, permission) => {
  if (!permission) return true;
  if (user?.authority === "superAdmin") return true;
  if (user?.accountHolder || user?.authority === "owner") return true;
  return getEffectivePermissions(user).includes(permission);
};
